"use strict";
import { useQuery } from "@tanstack/react-query";
import { ONE } from "@uniswap/router-sdk";
import { CurrencyAmount, Fraction, Percent, Price, TradeType } from "@uniswap/sdk-core";
import { useAccountMeta } from "contexts/UniswapContext";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { UniverseChainId } from "features/chains/types";
import {
  TOPAZ_API_NATIVE_TOKEN,
  fetchTopazApiQuote
} from "features/routing/api/topazRoutingApi";
import { useCurrencyInfo } from "features/tokens/useCurrencyInfo";
import { useTransactionSettingsContext } from "features/transactions/settings/contexts/TransactionSettingsContext";
import { useEip5792Support } from "features/transactions/swap/contexts/hooks/useEip5792Support";
import { useCallback, useMemo, useState } from "react";
import { QuoteMethod } from "state/routing/types";
import { buildCurrencyId } from "utils/uniswapCurrencyId";
function createTopazApiTradeObject(response, request, currencyIn, currencyOut, tradeType, slippageTolerance) {
  const inputAmount = CurrencyAmount.fromRawAmount(currencyIn, response.amount);
  const outputAmount = CurrencyAmount.fromRawAmount(currencyOut, response.quote);
  const minOutAmount = response.minimumAmountOut ? CurrencyAmount.fromRawAmount(currencyOut, response.minimumAmountOut) : outputAmount;
  const executionPrice = new Price(
    currencyIn,
    currencyOut,
    inputAmount.quotient.toString(),
    outputAmount.quotient.toString()
  );
  const slippagePercent = new Percent(Math.floor(slippageTolerance * 100), 1e4);
  return {
    inputAmount,
    outputAmount,
    tradeType,
    executionPrice,
    minimumAmountOut(_slippage) {
      if (tradeType === TradeType.EXACT_OUTPUT) {
        return outputAmount;
      }
      if (!_slippage && minOutAmount) {
        return minOutAmount;
      }
      const st = _slippage || slippagePercent;
      const adjusted = new Fraction(ONE).subtract(st).multiply(outputAmount.quotient).quotient;
      return CurrencyAmount.fromRawAmount(currencyOut, adjusted);
    },
    maximumAmountIn(slippage) {
      const st = slippage || slippagePercent;
      if (tradeType === TradeType.EXACT_INPUT) {
        return inputAmount;
      }
      const adjusted = new Fraction(ONE).add(st).multiply(inputAmount.quotient).quotient;
      return CurrencyAmount.fromRawAmount(currencyIn, adjusted);
    },
    worstExecutionPrice(slippage) {
      const st = slippage || slippagePercent;
      const minOut = tradeType === TradeType.EXACT_OUTPUT ? outputAmount : minOutAmount || CurrencyAmount.fromRawAmount(
        currencyOut,
        new Fraction(ONE).subtract(st).multiply(outputAmount.quotient).quotient
      );
      return new Price(currencyIn, currencyOut, inputAmount.quotient.toString(), minOut.quotient.toString());
    },
    inputTax: new Percent(0),
    outputTax: new Percent(0),
    priceImpact: void 0,
    gasUseEstimate: response.gasUseEstimate ? parseInt(response.gasUseEstimate, 10) : 3e5,
    gasUseEstimateUSD: "0.01",
    quoteMethod: QuoteMethod.CLIENT_SIDE_FALLBACK,
    approveInfo: { needsApprove: false },
    routing: "CLASSIC",
    fillType: "classic",
    isTopazApi: true,
    topazApiResponse: response,
    // The exact request the quote was built from, so the permit flow can re-POST
    // it verbatim (plus the signed permit) at execution time.
    topazApiRequest: request,
    v2Routes: [],
    v3Routes: [],
    mixedRoutes: [],
    swaps: [],
    routes: [],
    postSwapFeeOutputAmount: outputAmount,
    swapFee: void 0,
    blockNumber: response.blockNumber?.toString(),
    requestId: void 0,
    totalGasUseEstimateUSD: void 0,
    slippageTolerance,
    slippageTolerancePercent: slippagePercent
  };
}
export function useTopazApiQuote({
  tokenIn,
  tokenOut,
  amountSpecified,
  tradeType,
  chainId,
  skip = false,
  currencyIn: currencyInOverride,
  currencyOut: currencyOutOverride
}) {
  const account = useAccountMeta();
  const { defaultChainId } = useEnabledChains();
  const effectiveChainId = chainId ?? defaultChainId;
  const { supported: supportsBatching } = useEip5792Support(effectiveChainId);
  const { customSlippageTolerance, autoSlippageTolerance, customDeadline } = useTransactionSettingsContext();
  const slippageValue = customSlippageTolerance ?? autoSlippageTolerance ?? 0.5;
  const slippageBips = Math.floor(slippageValue * 100);
  const deadlineSeconds = customDeadline ? customDeadline * 60 : void 0;
  const currencyInInfo = useCurrencyInfo(
    tokenIn && effectiveChainId ? buildCurrencyId(effectiveChainId, tokenIn) : void 0
  );
  const currencyOutInfo = useCurrencyInfo(
    tokenOut && effectiveChainId ? buildCurrencyId(effectiveChainId, tokenOut) : void 0
  );
  const currencyIn = currencyInOverride ?? currencyInInfo?.currency;
  const currencyOut = currencyOutOverride ?? currencyOutInfo?.currency;
  const apiTokenIn = currencyIn?.isNative ? TOPAZ_API_NATIVE_TOKEN : tokenIn;
  const apiTokenOut = currencyOut?.isNative ? TOPAZ_API_NATIVE_TOKEN : tokenOut;
  const shouldSkip = useMemo(
    () => skip || !tokenIn || !tokenOut || !amountSpecified || amountSpecified === "0" || !currencyIn || !currencyOut || tokenIn === tokenOut || tradeType !== TradeType.EXACT_INPUT || // The routing API serves BSC mainnet only.
    effectiveChainId !== UniverseChainId.Mainnet,
    [skip, tokenIn, tokenOut, amountSpecified, currencyIn, currencyOut, tradeType, effectiveChainId]
  );
  const hasRecipient = Boolean(account?.address);
  const [refreshKey, setRefreshKey] = useState(0);
  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: [
      "topaz-api-quote",
      effectiveChainId,
      apiTokenIn,
      apiTokenOut,
      amountSpecified,
      // The queryFn hardcodes exactIn, so the key must too: without it, a cached
      // exactIn response for the same raw amount would be served to an exactOut
      // request (the enabled gate skips fetching but does not clear cached data).
      tradeType,
      slippageBips,
      deadlineSeconds,
      hasRecipient,
      account?.address,
      refreshKey,
      supportsBatching
    ],
    queryFn: async () => {
      if (!apiTokenIn || !apiTokenOut || !amountSpecified) {
        return null;
      }
      const request = {
        tokenIn: apiTokenIn,
        tokenOut: apiTokenOut,
        amount: amountSpecified,
        type: "exactIn",
        permitGrantedInBatch: supportsBatching,
        recipient: account?.address,
        slippageBips,
        deadlineSeconds
      };
      const response = await fetchTopazApiQuote(request);
      return { response, request };
    },
    enabled: !shouldSkip,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1
  });
  const quoteUpdatedAt = dataUpdatedAt || null;
  const trade = useMemo(() => {
    if (!data || !currencyIn || !currencyOut || tradeType !== TradeType.EXACT_INPUT) {
      return null;
    }
    return createTopazApiTradeObject(data.response, data.request, currencyIn, currencyOut, tradeType, slippageValue);
  }, [data, currencyIn, currencyOut, tradeType, slippageValue]);
  return useMemo(
    () => ({
      trade,
      isLoading,
      error,
      refetch,
      quoteUpdatedAt
    }),
    [trade, isLoading, error, refetch, quoteUpdatedAt]
  );
}
